import React, { useState, useEffect } from "react";
import {
    Card,
    Divider,
    Button,
    Drawer,
    Form,
    Input,
    Select,
    message,
    Space,
    Tag,
    Row,
    Col,
    Checkbox,
} from "antd";
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { mapZnsAPIWithErrorHandling } from "../../../api/zns/zns";
import dayjs from "dayjs";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const { Option } = Select;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
    "zns_event_id",
    "template_id",
    "oa",
    "acc_payment_id",
    "created_at",
];

export default function MapZnsTable() {
    const [form] = Form.useForm();

    // State management
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [events, setEvents] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
    });
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [templatePreview, setTemplatePreview] = useState(null);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [availableAliases, setAvailableAliases] = useState([]);
    const isAccountPay = Form.useWatch("is_account_pay", form);
    const selectedEventId = Form.useWatch("zns_event_id", form);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Load data on component mount
    useEffect(() => {
        loadData();
        loadEvents();
        fetchAccount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedEventId) {
            fetchAvailableAliases(selectedEventId);
        } else {
            setAvailableAliases([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    const fetchAvailableAliases = async (eventId) => {
        try {
            const response = await mapZnsAPIWithErrorHandling.getAvailableVariableAliases({ event_id: eventId });
            if (response.data && response.data.success) {
                setAvailableAliases(response.data.data || []);
            }
        } catch (error) {
            console.error("fetchAvailableAliases error:", error);
            setAvailableAliases([]);
        }
    };

    // Load ZNS mappings
    const loadData = async (params = {}) => {
        try {
            setLoading(true);
            const response = await mapZnsAPIWithErrorHandling.getMapZnss({
                page: pagination.current,
                per_page: pagination.pageSize,
                ...params,
            });

            if (response.data.success) {
                const { data: responseData } = response.data;
                setData(responseData.data);
                setPagination({
                    ...pagination,
                    current: responseData.current_page,
                    total: responseData.total,
                });
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Load events for select options
    const loadEvents = async () => {
        try {
            const response = await mapZnsAPIWithErrorHandling.getEventZnsAll();
            if (response.data.success) {
                setEvents(response.data.data);
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    const fetchAccount = async () => {
        try {
            const response = await paymentAccountAPIWithErrorHandling.getAccountAll();
            const groupData = response?.data || [];
            setAccounts(groupData);
        } catch (error) {
            message.error("Không thể tải danh sách tài khoản: " + error.message);
        }
    };

    // Handle pagination change
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
        loadData({
            page: newPagination.current,
            per_page: newPagination.pageSize,
        });
    };

    // Show modal for add/edit
    const showModal = (record = null) => {
        setEditingRecord(record);
        setModalVisible(true);

        if (record) {
            form.setFieldsValue({
                zns_event_id: record.zns_event_id,
                template_id: record.template_id,
                oa: record.oa,
                acc_payment_id: record.acc_payment_id,
                is_account_pay: record.acc_payment_id ? true : false,
                variable: record.variable || {},
            });
            if (record.template_id) {
                handlePreviewTemplate(record.template_id);
            }
        } else {
            form.resetFields();
            setTemplatePreview(null);
            setAvailableAliases([]);
        }
    };

    // Handle modal cancel
    const handleCancel = () => {
        setModalVisible(false);
        setEditingRecord(null);
        setTemplatePreview(null);
        setAvailableAliases([]);
        form.resetFields();
    };

    // Handle form submit
    const handleSubmit = async (values) => {
        try {
            setModalLoading(true);

            if (!!values.is_account_pay && !values.acc_payment_id) {
                message.warning("Vui lòng chọn tài khoản thanh toán trước");
                return;
            }

            if (editingRecord) {
                // Update existing record
                await mapZnsAPIWithErrorHandling.updateMapZns(editingRecord.id, values);
                message.success("Cập nhật liên kết ZNS thành công");
            } else {
                // Create new record
                await mapZnsAPIWithErrorHandling.createMapZns(values);
                message.success("Tạo liên kết ZNS thành công");
            }

            handleCancel();
            loadData();
        } catch (error) {
            message.error(error.message);
        } finally {
            setModalLoading(false);
        }
    };

    // Handle preview template
    const handlePreviewTemplate = async (templateId) => {
        if (!templateId) {
            message.warning("Vui lòng nhập Template ID");
            return;
        }

        try {
            setLoadingTemplate(true);
            const response = await mapZnsAPIWithErrorHandling.getTemplate({ template_id: templateId });

            if (response.data.success) {
                setTemplatePreview(response.data.data);
                message.success("Tải mẫu tin thành công");
            }
        } catch (error) {
            message.error(error.message || "Không thể tải mẫu tin");
            setTemplatePreview(null);
        } finally {
            setLoadingTemplate(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        const confirmed = await confirmDelete({ entity: "liên kết ZNS" });
        if (!confirmed) return;

        try {
            await mapZnsAPIWithErrorHandling.deleteMapZns(id);
            message.success("Xóa liên kết ZNS thành công");
            loadData();
        } catch (error) {
            message.error(error.message);
        }
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT",
                key: "index",
                width: 60,
                render: (_, record, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "zns_event_id", label: "Sự kiện",
            column: {
                title: "Sự kiện",
                dataIndex: "zns_event_id",
                key: "zns_event_id",
                render: (eventId) => {
                    const event = events.find((e) => e.id === eventId);
                    return event ? (
                        <Tag color="blue">{event.name}</Tag>
                    ) : (
                        <Tag color="red">Không xác định</Tag>
                    );
                },
            },
        },
        {
            key: "template_id", label: "Mẫu Template ID",
            column: {
                title: "Mẫu Template ID",
                dataIndex: "template_id",
                key: "template_id",
                render: (text) => <Tag color="green">{text}</Tag>,
            },
        },
        {
            key: "oa", label: "OA",
            column: {
                title: "OA",
                dataIndex: "oa",
                key: "oa",
            },
        },
        {
            key: "acc_payment_id", label: "Gắn tài khoản",
            column: {
                title: "Gắn tài khoản",
                dataIndex: "acc_payment_id",
                key: "acc_payment_id",
                render: (_, record) => {
                    const accpay = record?.acc_payment;

                    return accpay ? (
                        <Tag color="blue">{accpay?.bankcode} - {accpay?.name}</Tag>
                    ) : (
                        <Tag color="red">_</Tag>
                    );
                },
            },
        },
        {
            key: "created_at", label: "Ngày tạo",
            column: {
                title: "Ngày tạo",
                dataIndex: "created_at",
                key: "created_at",
                render: (_, record) => (
                    <>{dayjs(record.created_at).format("DD/MM/YYYY")}</>
                ),
            },
        },
        actionColumnDef(
            (record) => [
                { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
                { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record.id) },
            ],
            { width: 100 }
        ),
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);

    return (
        <BrandedPageLayout
            title="Cấu hình ZNS"
            subtitle="Quản lý liên kết mẫu ZNS theo sự kiện và tài khoản."
            breadcrumbItems={[{ title: "Cấu hình ZNS" }, { title: "Danh sách" }]}
        >
            <ListToolbar
                filterDefs={[]}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={false}
                onReload={() => loadData({ page: pagination.current, per_page: pagination.pageSize })}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => showModal()}
                    >
                        Thêm liên kết ZNS
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />

            <Drawer
                title={editingRecord ? "Chỉnh sửa liên kết ZNS" : "Thêm liên kết ZNS"}
                placement="right"
                width="min(1080px, 100vw)"
                open={modalVisible}
                onClose={handleCancel}
                destroyOnClose
                styles={{ body: { background: neutral[50], padding: spacing[6] } }}
                footer={
                    <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                        <Button onClick={handleCancel}>
                            Hủy
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            onClick={() => form.submit()}
                        >
                            {editingRecord ? "Cập nhật" : "Tạo mới"}
                        </Button>
                    </Space>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={24}>
                        {/* Cột trái - Form fields */}
                        <Col xs={24} lg={templatePreview ? 10 : 24}>
                            <Form.Item
                                name="zns_event_id"
                                label="Sự kiện ZNS"
                                rules={[
                                    { required: true, message: "Vui lòng chọn sự kiện ZNS" },
                                ]}
                            >
                                <Select
                                    placeholder="Chọn sự kiện ZNS"
                                    size="default"
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {events.map((event) => (
                                        <Option key={event.id} value={event.id}>
                                            {event.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="template_id"
                                label="Template ID"
                                rules={[
                                    { required: true, message: "Vui lòng nhập Template ID" },
                                ]}
                            >
                                <Input
                                    placeholder="Nhập Template ID"
                                    size="large"
                                    onChange={(e) => {
                                        if (!e.target.value) {
                                            setTemplatePreview(null);
                                        }
                                    }}
                                    addonAfter={
                                        <Button
                                            type="link"
                                            loading={loadingTemplate}
                                            onClick={() => {
                                                const templateId = form.getFieldValue('template_id');
                                                handlePreviewTemplate(templateId);
                                            }}
                                            icon={<EyeOutlined />}
                                            style={{ padding: '0 8px' }}
                                        >
                                            Xem mẫu
                                        </Button>
                                    }
                                />
                            </Form.Item>

                            <Form.Item
                                name="oa"
                                label="OA (Official Account)"
                                rules={[
                                    { required: true, message: "Vui lòng nhập tên OA" },
                                ]}
                            >
                                <Input placeholder="Nhập tên OA" size="default" />
                            </Form.Item>

                            <Form.Item
                                name="is_account_pay"
                                valuePropName="checked"
                            >
                                <Checkbox className="">Mẫu kèm tài khoản ngân hàng</Checkbox>
                            </Form.Item>

                            <Form.Item
                                name="acc_payment_id"
                                style={{ margin: 0, width: '100%' }}
                                rules={[{ required: false, message: "Chọn tài khoản" }]}
                            >
                                <Select
                                    style={{ width: "100%" }}
                                    placeholder="Chọn tài khoản"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={accounts.map((item) => ({
                                        value: item.id,
                                        label: item.name,
                                    }))}
                                    disabled={!isAccountPay}
                                />
                            </Form.Item>

                            {/* Mapping variables */}
                            {availableAliases.length > 0 && templatePreview && templatePreview.param && (
                                <>
                                    <Divider orientation="left" style={{ fontSize: '14px', margin: '16px 0', borderColor: '#ccc' }}>
                                        Bản đồ dữ liệu biến
                                    </Divider>
                                    <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #F1F5F9' }}>
                                        <Row gutter={[16, 0]}>
                                            {templatePreview.param.split(',').map(p => {
                                                const rawParam = p.trim();
                                                // Loại bỏ các dấu ngoặc vuông [], {}, <> để key lưu vào DB sạch sẽ
                                                const cleanParam = rawParam.replace(/[[\]{}<>]/g, '').trim();

                                                return (
                                                    <Col span={24} key={cleanParam}>
                                                        <Form.Item
                                                            name={['variable', cleanParam]}
                                                            label={<span style={{ fontWeight: 500 }}>Tham số từ Template: <Tag color="geekblue">{cleanParam}</Tag></span>}
                                                            style={{ marginBottom: 16 }}
                                                        >
                                                            <Select placeholder="Chọn biến Hệ thống lấy dữ liệu" allowClear showSearch>
                                                                {availableAliases.map(alias => (
                                                                    <Option key={alias} value={alias}>{alias}</Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </div>
                                </>
                            )}
                        </Col>

                        {/* Cột phải - Template Preview */}
                        {templatePreview && (
                            <Col xs={24} lg={14}>
                                <div style={{
                                    position: 'sticky',
                                    top: 0,
                                }}>
                                    <Card
                                        title={
                                            <Space>
                                                <span>Xem trước mẫu tin</span>
                                            </Space>
                                        }
                                        size="small"
                                        style={{
                                            backgroundColor: '#F8FAFC',
                                            border: '2px solid #0070D7',
                                            borderRadius: '8px'
                                        }}
                                        headStyle={{
                                            backgroundColor: '#F0F6FD',
                                            borderBottom: '1px solid #B8D7F4'
                                        }}
                                    >
                                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                            <div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#64748B',
                                                    marginBottom: '8px'
                                                }}>
                                                    Nội dung tin nhắn
                                                </div>
                                                <div style={{
                                                    whiteSpace: 'pre-wrap',
                                                    backgroundColor: 'white',
                                                    padding: '16px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #CBD5E1',
                                                    minHeight: '100px',
                                                    fontSize: '14px',
                                                    lineHeight: '1.6',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                                                    overflowY: 'scroll',
                                                    height: '200px'
                                                }}>
                                                    {templatePreview.content}
                                                </div>
                                            </div>

                                            <div>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#64748B',
                                                    marginBottom: '8px'
                                                }}>
                                                    Tham số động ({templatePreview.param.split(',').length})
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {templatePreview.param.split(',').map((param, index) => {
                                                        const cleanParam = param.replace(/[[\]{}<>]/g, '').trim();
                                                        return (
                                                            <Tag
                                                                key={index}
                                                                color="blue"
                                                                style={{
                                                                    margin: 0,
                                                                    fontSize: '13px',
                                                                    padding: '4px 12px'
                                                                }}
                                                            >
                                                                {cleanParam}
                                                            </Tag>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </Space>
                                    </Card>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Drawer>
        </BrandedPageLayout>
    );
}
